import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, SendHorizonal, ChevronDown, ChevronRight } from "lucide-react";
import { API, api } from "@/lib/api";

const CATEGORIES = {
  relance_opportunite: { label: "Relances", couleur: "#7C5CE0" },
  rappel_essai: { label: "Rappels d'essai", couleur: "#16A34A" },
  invitation_essai: { label: "Invitations essai", couleur: "#16A34A" },
  post_essai_proposition: { label: "Suites d'essai", couleur: "#16A34A" },
  post_essai_alternative: { label: "Suites d'essai", couleur: "#16A34A" },
  impaye: { label: "Impayés", couleur: "#DC2626" },
  repositionnement: { label: "VO âgés", couleur: "#E8853D" },
  rupture: { label: "Ruptures pièces", couleur: "#DC2626" },
  sous_seuil: { label: "Pièces sous seuil", couleur: "#B45309" },
  creux: { label: "Creux planning", couleur: "#2F6FED" },
  offre_dormante: { label: "Dossiers dormants", couleur: "#64748B" },
  vente_additionnelle: { label: "Ventes additionnelles", couleur: "#0D9488" },
  ecart_site: { label: "Écarts sites", couleur: "#DC2626" },
  dossier_bloque_critique: { label: "Dossiers bloqués", couleur: "#DC2626" },
  dossier_bloque_eleve: { label: "Dossiers bloqués", couleur: "#B45309" },
  cg_non_remise_30j: { label: "Cartes grises à remettre", couleur: "#0369A1" },
  prime_a_declarer: { label: "Primes constructeur", couleur: "#16A34A" },
  commande_usine_sans_maj: { label: "Commandes usine", couleur: "#E8853D" },
  signature_en_attente_5j: { label: "Signatures en attente", couleur: "#7C5CE0" },
  reprise_non_entree_livree: { label: "Reprises à régulariser", couleur: "#DC2626" },
  piece_manquante_livraison_10j: { label: "Pièces avant livraison", couleur: "#B45309" },
};
const MODULE_ROUTES = { facturation: "/facturation", stock_vehicules: "/stock-vehicules", stock_pieces: "/stock-pieces", planning: "/planning", dossiers: "/ventes", or: "/ordres", pipeline: "/pipeline", dashboard: "/" };

function ResumeSuggestions({ open, onClose }) {
  const [sugs, setSugs] = useState(null);
  const [cat, setCat] = useState(null);
  const [replie, setReplie] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/copilot/suggestions?module=all").then((r) => setSugs(r.data.suggestions)).catch(() => setSugs([]));
      setCat(null);
    }
  }, [open]);

  if (!sugs || sugs.length === 0) return null;
  const groupes = {};
  sugs.forEach((s) => {
    const c = CATEGORIES[s.type] || { label: "Autres", couleur: "#64748B" };
    groupes[c.label] = groupes[c.label] || { ...c, items: [] };
    groupes[c.label].items.push(s);
  });
  return (
    <div className="border-b border-[--idc-border] bg-[#F8FAFD]" data-testid="copilot-resume-suggestions">
      <button className="w-full flex items-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold" onClick={() => setReplie(!replie)} data-testid="copilot-resume-toggle">
        {replie ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {sugs.length} suggestion{sugs.length > 1 ? "s" : ""} active{sugs.length > 1 ? "s" : ""}
      </button>
      {!replie && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1.5">
            {Object.values(groupes).map((g) => (
              <button key={g.label} onClick={() => setCat(cat === g.label ? null : g.label)}
                className="pill transition-opacity hover:opacity-80"
                style={{ background: cat === g.label ? g.couleur : `${g.couleur}1A`, color: cat === g.label ? "#fff" : g.couleur, border: `1px solid ${g.couleur}40` }}
                data-testid={`copilot-cat-${g.label.replace(/[' ]/g, "-")}`}>
                {g.items.length} {g.label}
              </button>
            ))}
          </div>
          {cat && (
            <div className="mt-2 space-y-1 max-h-44 overflow-y-auto" data-testid="copilot-cat-liste">
              {groupes[cat].items.map((s) => (
                <Link key={s.id} to={s.payload?.lien || MODULE_ROUTES[s.module] || "/"} onClick={onClose}
                  className="block text-[12px] px-2.5 py-1.5 rounded-lg bg-white border border-[--idc-border] hover:border-[--idc-accent] transition-colors truncate"
                  data-testid={`copilot-sug-${s.id}`} title={s.constat}>
                  {s.titre}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RenduMessage({ texte }) {
  const lignes = String(texte).split("\n");
  return (
    <div className="space-y-1">
      {lignes.map((l, i) => {
        const parts = [];
        let rest = l;
        let key = 0;
        const re = /\[([^\]]+)\]\((\/[^)]*)\)/;
        while (true) {
          const m = rest.match(re);
          if (!m) break;
          const before = rest.slice(0, m.index);
          if (before) parts.push(<span key={key++}>{before}</span>);
          parts.push(
            <Link key={key++} to={m[2]} className="text-[--idc-accent] font-medium hover:underline">
              {m[1]}
            </Link>
          );
          rest = rest.slice(m.index + m[0].length);
        }
        if (rest) parts.push(<span key={key++}>{rest.replace(/\*\*/g, "")}</span>);
        return <p key={i} className="leading-relaxed">{parts}</p>;
      })}
    </div>
  );
}

const QUESTIONS_RAPIDES = [
  "Quel est mon CA facturé ce mois-ci ?",
  "Quels VO ont plus de 60 jours de stock ?",
  "Résume la journée atelier de demain",
  "Quelles factures sont en retard ?",
];

export default function CopilotPanel({ open, onClose, enLigne }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [enCours, setEnCours] = useState(false);
  const bas = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/copilot/messages?session_id=default").then((r) =>
        setMessages(r.data.map((m) => ({ role: m.role, contenu: m.contenu })))
      ).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    bas.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const envoyer = async (texte) => {
    const msg = (texte || input).trim();
    if (!msg || enCours) return;
    setInput("");
    setEnCours(true);
    setMessages((m) => [...m, { role: "user", contenu: msg }, { role: "assistant", contenu: "" }]);
    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("idcore_token")}` },
        body: JSON.stringify({ message: msg, session_id: "default" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(data);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", contenu: copy[copy.length - 1].contenu + delta };
              return copy;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", contenu: "Erreur de connexion à ID Copilot. Réessayez." };
        return copy;
      });
    }
    setEnCours(false);
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-y-0 right-0 w-full sm:w-[380px] bg-white border-l border-[--idc-border] shadow-2xl z-50 flex flex-col`} data-testid="copilot-panel">
      <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-[--idc-border]" style={{ background: "linear-gradient(90deg, #0E2A4E, #16326E)" }}>
        <Sparkles className="w-4.5 h-4.5 text-white w-5 h-5" />
        <div className="flex-1">
          <div className="text-white font-semibold text-sm">ID Copilot</div>
          <div className="text-[11px] text-[#9DB4D6]">{enLigne ? "En ligne — propulsé par Claude" : "Mode local (sans clé API)"}</div>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="copilot-close-btn" aria-label="Fermer">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!enLigne && (
        <div className="text-[11.5px] text-[#8A6D1D] bg-[#FEF7E0] px-4 py-2 border-b border-[#F5E7B8]" data-testid="copilot-banner-local">
          ID Copilot en mode local — suggestions issues des règles métier seules.
        </div>
      )}

      <ResumeSuggestions open={open} onClose={onClose} />

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="copilot-messages">
        {messages.length === 0 && (
          <div className="pt-4">
            <p className="text-sm text-[--idc-muted] mb-3">
              Posez une question en français sur vos données : planning, stocks, factures, ventes…
            </p>
            <div className="space-y-2">
              {QUESTIONS_RAPIDES.map((qr) => (
                <button
                  key={qr}
                  onClick={() => envoyer(qr)}
                  className="w-full text-left text-[13px] px-3 py-2 rounded-lg border border-[--idc-border] hover:border-[--idc-accent] hover:bg-[#EFF4FF] transition-colors"
                  data-testid="copilot-question-rapide"
                >
                  {qr}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`text-[13px] ${m.role === "user" ? "ml-8" : "mr-4"}`}>
            <div
              className={`rounded-xl px-3.5 py-2.5 ${
                m.role === "user" ? "bg-[#16326E] text-white" : "bg-[#F2F5F9] text-[--idc-text]"
              }`}
            >
              {m.role === "assistant" ? (
                m.contenu ? <RenduMessage texte={m.contenu} /> : <span className="inline-flex gap-1"><span className="animate-pulse">●</span><span className="animate-pulse [animation-delay:150ms]">●</span><span className="animate-pulse [animation-delay:300ms]">●</span></span>
              ) : (
                m.contenu
              )}
            </div>
          </div>
        ))}
        <div ref={bas} />
      </div>

      <div className="p-3 border-t border-[--idc-border]">
        <div className="flex items-center gap-2 bg-[#F2F5F9] rounded-full px-4 py-1.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && envoyer()}
            placeholder="Posez une question sur vos données…"
            className="flex-1 bg-transparent outline-none text-sm py-1.5"
            data-testid="copilot-input"
          />
          <button
            onClick={() => envoyer()}
            disabled={enCours || !input.trim()}
            className="text-[--idc-accent] disabled:opacity-40 p-1"
            data-testid="copilot-send-btn"
            aria-label="Envoyer"
          >
            <SendHorizonal className="w-4.5 h-4.5 w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
